// The one place the app talks to the network. Every 401 funnels to the session gate;
// callers never see auth errors. Response bodies are typed by the caller — the server
// already validated and role-shaped them (never trust the client, always the server).
import { signal } from '@preact/signals';

/** Flipped by any 401; the App renders the passcode gate while true. */
export const authRequired = signal(false);

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: Record<string, unknown>,
  ) {
    super(`api ${status}`);
  }
}

async function handle<T>(res: Response): Promise<T> {
  if (res.status === 401) {
    authRequired.value = true;
    throw new ApiError(401, {});
  }
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) throw new ApiError(res.status, body);
  return body as T;
}

export async function apiGet<T>(path: string): Promise<T> {
  return handle<T>(await fetch(path, { headers: { Accept: 'application/json' } }));
}

export async function apiPost<T>(
  path: string,
  body: unknown,
  opts: { idempotencyKey?: string } = {},
): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
  return handle<T>(await fetch(path, { method: 'POST', headers, body: JSON.stringify(body) }));
}

export async function apiUpload<T>(path: string, bytes: Blob, idempotencyKey: string): Promise<T> {
  return handle<T>(
    await fetch(path, {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
      body: bytes,
    }),
  );
}
